import { Carousel } from "antd";
import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { BsHourglassSplit, BsPinAngleFill } from "react-icons/bs";
import "../StayUpToDateComponent/StayUpToDate.css";
import "./ShopCardAlignment.css";
import ImagePostTag from "./ImagePostTag";
import VideoPostTag from "./VideoPostTag";
import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import {
  getMediaSrc,
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import IndividualYoutubePostTag from "../StayUpToDateComponent/StayUpToDateIndividuelComponent/IndividualYoutubePostTag";
import { preserveBrokenImageBox } from "../../Utils/imageUtils";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import {
  useTemplateMediaIndex,
  useTemplateMediaReveal,
} from "../../Screens/DashboardView/MasterTemplateEditor/useTemplateMediaReveal";
import "../../Screens/DashboardView/MasterTemplateEditor/templateMediaReveal.css";
import ProductBankImageFrame from "../ProductBank/ProductBankImageFrame";
import { shouldUseProductImageFrame } from "../../Utils/productImage/productImageDisplay";
import {
  getThumbnail,
  isLikelyVideoUrl,
  listPostMediaUrls,
} from "../../Utils/media/feedMedia";

const ImagePostTagCarousel = React.memo((slideProps: any) => {
  const {
    media_url,
    isVideo,
    post: slidePost,
    handleRedirect: slideRedirect,
    productName: slideProductName,
    contentShape: slideShape,
    from: slideFrom,
    designTokens: slideTokens,
    pinnedPostIds: pinnedPostIdsProp,
    slideIndex = 0,
    isTemplateSite,
    gridIndex,
    useProductFrame,
    isLinks,
    title,
    brand_color,
    contentShape,
    height,
    source,
    imageClassName,
    shouldLoad,
    priority,
  } = slideProps;
    const pinIds = pinnedPostIdsProp;
    const resolvedSrc = shouldLoad
    ? resolveFeedCardImage(media_url, Boolean(priority)).src
    : "";
  const {
    setRef,
    activeSrc,
    onLoad,
    onError,
    wrapperClassName: revealClass,
  } = useTemplateMediaReveal({
    enabled: isTemplateSite && shouldLoad && Boolean(media_url),
    src: resolvedSrc,
    index: typeof gridIndex === "number" ? gridIndex : slideIndex,
    priority: Boolean(priority),
  });

  const isPinned = Array.isArray(pinIds)
    ? pinIds.includes(slidePost?.post_id)
    : false;

  const renderPinnedBadge = () =>
    isPinned && source !== "edit" ? (
      <div className="pinned-badge" aria-label="Pinned post" style={{right:"4px"}}>
        <BsPinAngleFill />
      </div>
    ) : null;

  const redirectUrl: any = redirectToUrl(slidePost, isLinks);
  const permalinkUrl: any = slidePost?.permalink || redirectUrl;
  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;
    slideRedirect(
      slidePost?.post_id,
      getMediaSrc(undefined, slidePost, true, media_url) ||
        slidePost?.thumbnail ||
        media_url,
      slidePost?.platform,
      slidePost?.product_name,
      title,
      isLinks,
      slideFrom,
    );
    openOutboundUrl(targetUrl);
  };
  // If YouTube and no product_image, render IndividualYoutubePostTag
  if (slidePost?.platform === "youtube" && !slidePost?.product_image) {
    return (
      <IndividualYoutubePostTag
        link={slidePost}
        handleRedirect={slideRedirect}
        bgColor={brand_color}
        contentShape={slideShape}
        redirectUrl={redirectUrl}
        source={source}
        pinnedPostIds={pinnedPostIdsProp}
        from={slideFrom}
        designTokens={slideTokens}
        showShopButton={false}
      />
    );
  }
  const imgSrc = isTemplateSite ? activeSrc : resolvedSrc;
  if (!shouldLoad) {
    return <div className={`${imageClassName} feed-card-media-slot`} />;
  }
  return (
    <>
      <div
        ref={isTemplateSite ? (setRef as React.Ref<HTMLDivElement>) : undefined}
        className={revealClass || undefined}
        style={{ position: "relative", display: "block", cursor: "pointer" }}
        onClick={() => openUrlWithTracking(permalinkUrl)}
      >
        {renderPinnedBadge()}
        {media_url === "" ? (
          <div className={imageClassName}>
            <BsHourglassSplit className="hourglassSplit" />
            <p>
              Work in progress: This content will be available in a few
              minutes.
            </p>
          </div>
        ) : useProductFrame ? (
          <ProductBankImageFrame
            src={resolvedSrc}
            alt="post"
            fill
            shape={contentShape}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onError={isTemplateSite ? onError : preserveBrokenImageBox}
          />
        ) : imgSrc ? (
          <Card.Img
            variant="top"
            src={imgSrc}
            className={imageClassName}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onLoad={isTemplateSite ? onLoad : undefined}
            onError={isTemplateSite ? onError : preserveBrokenImageBox}
            {...(priority
              ? ({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)
              : {})}
            style={{
              marginBottom: isTemplateSite ? "2%" : "0%",
              height: height,
            }}
          />
        ) : null}
      </div>
    </>
  );
});

const ImagePostCarouselTag = (props: any) => {
  const {
    post,
    redirectVariable,
    isLinks,
    handleRedirect,
    title,
    brand_color,
    isDashboard,
    height,
    isYoutube,
    isHasMediaUrl,
    productName,
    type,
    contentShape,
    isSoundOn,

    isCarousel,
    from,
    designTokens,
    pinnedPostIds,
    mediaIndex,
    enableInteractiveMedia = true,
    disableHoverPlayback = false,
    source,
  } = props;

  const isTemplateSite = from === "website-template";
  /** Dots on media — no slide margin reserved below the tile for indicators. */
  const dotsOnMedia =
    isTemplateSite ||
    from === "landing-page" ||
    Boolean(designTokens) ||
    Boolean(isDashboard);
  const slideBottomPad = dotsOnMedia ? 0 : "8%";
  const ctxIndex = useTemplateMediaIndex();
  const gridIndex = typeof mediaIndex === "number" ? mediaIndex : ctxIndex;
  const useProductFrame = shouldUseProductImageFrame(type, post);

  const mediaUrls = listPostMediaUrls(post).slice(0, 10);
  const [loadedSlides, setLoadedSlides] = React.useState<number[]>([0, 1]);

  // iOS Safari only supports a handful of concurrently-decoding <video>
  // elements; a carousel used to mount a HoverVideoPlayer for every video
  // slide up front instead of just the one on screen, which multiplied the
  // number of simultaneously loaded videos on the page and could exhaust
  // Safari's memory/decoder budget (blank sections, tab reload) while
  // scrolling past several such carousels. Only the active slide gets a
  // real video player; other video slides fall back to a static poster.
  const [activeSlide, setActiveSlide] = useState(0);

  const getWrapperClassName = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];
    if (contentShape == "sq") classes.push("square-wrapper");
    // else if (isHasMediaUrl) classes.push("square-wrapper");
    else if (isYoutube) classes.push("youtube-feed-wrapper");

    return classes.join(" ");
  };

  const getImageClassName = () => {
    const baseClass = "creator-feed-card-links-image";
    const classes = [baseClass];

    if (contentShape == "sq") classes.push("square-image");
    // else if (isHasMediaUrl) classes.push("media-feed-height");
    else if (isYoutube) classes.push("youtube-feed-height");

    return classes.join(" ");
  };

  const onChange = (currentSlide: number) => {
    setActiveSlide(currentSlide);
    setLoadedSlides((prev) => {
      const next = new Set(prev);
      for (const n of [currentSlide - 1, currentSlide, currentSlide + 1]) {
        if (n >= 0 && n < mediaUrls.length) next.add(n);
      }
      return next.size === prev.length ? prev : Array.from(next);
    });
  };


  const RenderProductComponent = (props: any) => {
    const {
      media_url,
      isVideo,
      post,
      handleRedirect,
      productName,
      contentShape,
      from,
      designTokens,
      pinnedPostIds,
    } = props;

    const redirectUrl: any = redirectToUrl(post, isLinks);
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();

      if (redirectUrl) {
        handleRedirect(
          post?.post_id,
          getMediaSrc(undefined, post, true, post?.media_url) ||
            post?.thumbnail ||
            post?.media_url,
          post?.platform,
          post?.product_name,
          title,
          isLinks,
          from,
        );
        // For Safari mobile, use location.href instead of window.open
        openOutboundUrl(redirectUrl);
      }
    };

    return (
      <a
        href={redirectUrl}
        target="_blank"
        onClick={handleClick}
        className="carousel-product-link"
      >
        <ProductName type={type} post={post} designTokens={designTokens} />
        <span className="carousel-shop-btn-wrap">
          <ProductShopButton
            isLinks={isLinks}
            IsIndividual={false}
            type={type}
            post={post}
            isCarousel={true}
            isDashboard={isDashboard}
            asLink={false}
            designTokens={designTokens}
          />
        </span>
      </a>
    );
  };
  const wrapperClass = getWrapperClassName();
  const dotStyleVars = {
    "--carousel-dot-color": designTokens?.tertiaryColor || "#555454",
    "--carousel-dot-active-color": designTokens?.secondaryColor || "#000000",
  } as React.CSSProperties;
  const hasVideo = mediaUrls.some((media_url: string) =>
    isLikelyVideoUrl(media_url),
  );
  return (
    <>
      <span>
        <div
          className={`${wrapperClass} design-token-dots image-post-carousel`}
          style={{  ...dotStyleVars }}
        >
          <Carousel
            afterChange={onChange}
            dotPosition="bottom"
            dots={mediaUrls.length > 1}
            infinite={mediaUrls.length > 1}
            speed={isTemplateSite ? 0 : undefined}
            waitForAnimate={false}
            style={
              dotsOnMedia || isTemplateSite
                ? { marginBottom: 0 }
                : { marginBottom: "3px" }
            }
          >
            {mediaUrls.map((media_url: string, index: number) => {
              if (hasVideo) {
                return (
                  <div key={index}>
                    <div
                      className={`${wrapperClass} design-token-dots`}
                      style={{
                        marginBottom: slideBottomPad,
                        ...dotStyleVars,
                      }}
                    >
                      {!loadedSlides.includes(index) ? (
                        <div className={`${getImageClassName()} feed-card-media-slot`} />
                      ) : isLikelyVideoUrl(media_url) ? (
                        // Always VideoPostTag so poster error fallback runs —
                        // a static ImagePostTagCarousel left expired CDN thumbs broken.
                        <VideoPostTag
                          media_url={media_url}
                          productName={productName}
                          post={post}
                          mediaIndex={index}
                          thumbnail={getThumbnail(post, index)}
                          handleRedirect={handleRedirect}
                          title={title}
                          isSoundOn={isSoundOn}
                          type={type}
                          isCarousel={true}
                          contentShape={contentShape}
                          height={height}
                          brand_color={brand_color}
                          from={from}
                          designTokens={designTokens}
                          pinnedPostIds={pinnedPostIds}
                          source={source}
                          enableInteractiveMedia={
                            index === activeSlide && enableInteractiveMedia
                          }
                          disableHoverPlayback={disableHoverPlayback}
                        />
                      ) : (
                        <ImagePostTag
                          data={post}
                          title={title}
                          media_url={media_url}
                          isVideo={true}
                          handleRedirect={handleRedirect}
                          productName={productName}
                          isCarousel={true}
                          contentShape={contentShape}
                          height={height}
                          brand_color={brand_color}
                          from={from}
                          designTokens={designTokens}
                          pinnedPostIds={pinnedPostIds}
                          loading={index === 0 && activeSlide === 0 ? "eager" : "lazy"}
                          source={source}
                        />
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div
                    key={index}
                    className={wrapperClass}
                    style={{ marginBottom: slideBottomPad }}
                  >
                    {loadedSlides.includes(index) ? (
                    <ImagePostTagCarousel
                      post={post}
                      media_url={media_url}
                      handleRedirect={handleRedirect}
                      productName={productName}
                      isCarousel={true}
                      contentShape={contentShape}
                      height={height}
                      brand_color={brand_color}
                      from={from}
                      designTokens={designTokens}
                      pinnedPostIds={pinnedPostIds}
                      slideIndex={index}
                      isTemplateSite={isTemplateSite}
                      gridIndex={gridIndex}
                      useProductFrame={useProductFrame}
                      isLinks={isLinks}
                      title={title}
                      source={source}
                      imageClassName={getImageClassName()}
                      shouldLoad
                      priority={index === 0 && typeof gridIndex === "number" && gridIndex < 2}
                    />
                    ) : (
                      <div className={`${getImageClassName()} feed-card-media-slot`} />
                    )}
                  </div>
                );
              }
            })}
          </Carousel>

          <RenderProductComponent
            post={post}
            handleRedirect={handleRedirect}
            productName={productName}
            isCarousel={true}
            contentShape={contentShape}
            isLinks={isLinks}
            IsIndividual={false}
            type={type}
            from={from}
            designTokens={designTokens}
          />
        </div>
      </span>
    </>
  );
};

export default ImagePostCarouselTag;
